import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Contacto = () => {
  const navigate = useNavigate();
  const { ref: pageRef, isVisible } = useScrollAnimation();
  
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    mensaje: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    console.log('📧 Formulario de contacto enviado a customers@fieldstech.io:', formData);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitting(false);
    navigate('/gracias?origen=contacto');
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-24 sm:pt-28">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#0A1E3D]/90 via-[#0C2340]/90 to-[#0F172A]/90 py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
            Contacto
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 leading-relaxed">
            ¿Tienes dudas sobre cómo migrar tu sistema a Google Cloud? Escríbenos y un especialista te responderá en menos de 24 horas.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div ref={pageRef} className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16 lg:py-20">
        {/* Form */}
        <div className={`bg-white rounded-2xl shadow-lg p-6 sm:p-8 lg:p-10 mb-8 scroll-animate ${isVisible ? 'in-view' : ''}`}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombre */}
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                Nombre completo <span className="text-red-500">*</span>
              </label>
              <Input
                id="nombre"
                name="nombre"
                type="text"
                required
                value={formData.nombre}
                onChange={handleChange}
                className="w-full border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                placeholder="Juan Pérez"
              />
            </div>

            {/* Empresa */}
            <div>
              <label htmlFor="empresa" className="block text-sm font-medium text-gray-700 mb-2">
                Empresa <span className="text-red-500">*</span>
              </label>
              <Input
                id="empresa"
                name="empresa"
                type="text"
                required
                value={formData.empresa}
                onChange={handleChange}
                className="w-full border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                placeholder="Mi Empresa S.A. de C.V."
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Correo corporativo <span className="text-red-500">*</span>
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                placeholder="juan@miempresa.com"
              />
            </div>

            {/* Teléfono */}
            <div>
              <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                Teléfono de contacto
              </label>
              <Input
                id="telefono"
                name="telefono"
                type="tel"
                value={formData.telefono}
                onChange={handleChange}
                className="w-full border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                placeholder="+52 55 1234 5678"
              />
            </div>

            {/* Mensaje */}
            <div>
              <label htmlFor="mensaje" className="block text-sm font-medium text-gray-700 mb-2">
                ¿En qué podemos ayudarte? <span className="text-red-500">*</span>
              </label>
              <Textarea
                id="mensaje"
                name="mensaje"
                rows={6}
                required
                value={formData.mensaje}
                onChange={handleChange}
                className="w-full border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                placeholder="Cuéntanos sobre tu sistema actual (VB6, SQL Server, FoxPro...) o necesidades de modernización..."
              />
            </div>

            {/* Privacy Message */}
            <div className="flex items-start gap-3 bg-slate-50 rounded-lg p-4">
              <Shield className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600 leading-relaxed">
                Tus datos están protegidos bajo estricto acuerdo de confidencialidad y conforme a la LFPDPPP. Nunca compartimos tu información.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-6 text-base sm:text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enviando mensaje...' : 'Enviar mensaje'}
            </Button>
          </form>
        </div>

        {/* Contact Info */}
        <div className="space-y-6">
          <div className={`bg-white rounded-2xl shadow-md p-6 scroll-animate delay-200 ${isVisible ? 'in-view' : ''}`}>
            <div className="flex items-center justify-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-emerald-500" />
              </div>
              <div className="text-center">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  Correo Electrónico
                </h3>
                <a 
                  href="mailto:customers@fieldstech.io"
                  className="text-emerald-500 hover:text-emerald-600 transition-colors text-lg"
                >
                  customers@fieldstech.io
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contacto;
